import ReportcenterWidget from 'App/pages/Reportcenter/ReportcenterWidget';
import ReportWidget, { RCWidgetDataRowShape, RCWidgetMeta } from 'Resources/ReportWidget';
import React, { ComponentProps, useEffect, useState } from 'react';
import { makeCancelable } from 'App/babel/helpers/promise';
import { useFeatureAvailable, useSoftDeployAccess } from 'App/components/hooks';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { useStages } from 'App/components/hooks/appHooks';
import BemClass from '@upsales/components/Utils/bemClass';
import { Link, Loader } from '@upsales/components';
import Client from 'App/resources/Model/Client';
import T from 'Components/Helpers/translate';
import { useSelector } from 'react-redux';
import logError from 'Helpers/logError';
import { RootState } from 'Store/index';
import moment from 'moment';

import './SalesGraph.scss';
import EventBus from 'App/services/EventBus';

export enum Groupings {
	YEAR = 'year',
	FISCAL_YEAR = 'fiscalYear',
	MONTH = 'month',
	USER = 'user',
	STAGE = 'stage',
	CLIENT = 'client',
	PRODUCT = 'product',
	PRODUCT_CATEGORY = 'productCategory',
	ROOT_PRODUCT_CATEGORY = 'rootProductCategory'
}

export enum WidgetType {
	RECURRING = 'RECURRING',
	CASH_FLOW = 'SALES',
	CONTRIBUTION_MARGIN = 'CONTRIBUTION_MARGIN',
	TOTAL_RECURRING = 'TOTAL_RECURRING',
	PIPELINE = 'PIPELINE',
	PRODUCT = 'CURRENT_RECURRING'
}

const widgetTypeToQuotaType: Record<WidgetType, 'sales' | 'recurring' | 'contributionMargin' | 'default'> = {
	[WidgetType.RECURRING]: 'recurring',
	[WidgetType.TOTAL_RECURRING]: 'recurring',
	[WidgetType.CASH_FLOW]: 'sales',
	[WidgetType.CONTRIBUTION_MARGIN]: 'contributionMargin',
	[WidgetType.PIPELINE]: 'default',
	[WidgetType.PRODUCT]: 'default'
};

export const getClientQuotaType = (
	widgetType: WidgetType,
	hasCM: boolean,
	hasRecurring: boolean
): 'sales' | 'recurring' | 'contributionMargin' => {
	const quotaType = widgetTypeToQuotaType[widgetType];
	if (quotaType === 'default' || !quotaType) {
		return hasCM ? 'contributionMargin' : hasRecurring ? 'recurring' : 'sales';
	}
	return quotaType;
};

type Props = {
	clientId: Client['id'];
	grouping: Groupings;
	widgetType: WidgetType;
	subAccountIds?: number[];
	hideGoal?: boolean;
	height?: number;
	isGroupBySubAccount?: boolean;
};

const fillEmptyMonths = (rows: RCWidgetDataRowShape[]): RCWidgetDataRowShape[] => {
	if (!rows.length) {
		return rows;
	}

	const rowsWithDates = rows
		.map(row => ({
			row,
			date: moment(row.key)
		}))
		.filter(item => item.date.isValid());

	// need two rows to pick the earliest and latest dates
	if (rowsWithDates.length < 2) {
		return rows;
	}

	const earliest = moment.min(rowsWithDates.map(r => r.date));
	const latest = moment.max(rowsWithDates.map(r => r.date));

	// Create a map of existing rows by month key
	const rowMap = new Map<string, RCWidgetDataRowShape>();
	rowsWithDates.forEach(({ row, date }) => {
		const key = date.format('YYYY-MM');
		rowMap.set(key, row);
	});

	//Fill in missing months
	const filledRows: RCWidgetDataRowShape[] = [];
	const current = earliest.clone();

	while (current.isSameOrBefore(latest, 'month')) {
		const key = current.format('YYYY-MM');

		if (rowMap.has(key)) {
			filledRows.push(rowMap.get(key)!);
		} else {
			// Create empty row for missing month
			filledRows.push({
				key,
				label: current.format('MMM YYYY'),
				sorting: current.clone().endOf('month').toISOString(),
				progress: 0,
				remaining: 0,
				goal: 0,
				rows: []
			});
		}
		current.add(1, 'month');
	}

	return filledRows;
};

const SalesGraph = ({
	clientId,
	grouping,
	widgetType,
	subAccountIds = [],
	hideGoal = false,
	height = 3,
	isGroupBySubAccount
}: Props) => {
	const [widgetData, setWidgetData] = useState<null | ComponentProps<typeof ReportcenterWidget>['widgetData']>(null);
	const [widgetError, setWidgetError] = useState(false);
	const [widgetMeta, setWidgetMeta] = useState<undefined | { [k: string]: RCWidgetMeta }>();
	const hasSubaccountsV2 = useSoftDeployAccess('SUB_ACCOUNTS_V2');
	const hasSubaccountsFeature = useFeatureAvailable(Feature.SUB_ACCOUNTS) && hasSubaccountsV2;
	const hasCompanyGroupCardFeature = useFeatureAvailable(Feature.COMPANY_GROUP_CARD);
	const excludedStageIds = useStages()
		.filter(s => s.exclude)
		.map(stage => stage.id);

	const { metadata } = useSelector(({ App }: RootState) => App);
	const { SalesModel } = metadata?.params || {};
	let showGoal = false;
	if (grouping !== Groupings.USER && grouping !== Groupings.STAGE) {
		if (
			(SalesModel === 'rr' && widgetType === WidgetType.RECURRING) ||
			(SalesModel === 'rr' && widgetType === WidgetType.CASH_FLOW) ||
			(SalesModel === 'cm' && widgetType === WidgetType.CONTRIBUTION_MARGIN) ||
			(SalesModel === 'cm' && widgetType === WidgetType.CASH_FLOW) ||
			(SalesModel === 'sales' && widgetType === WidgetType.CASH_FLOW)
		) {
			showGoal = true;
		}
	}

	const classes = new BemClass('SalesGraph');

	const fetchWidgetData = () => {
		const q: object[] = [];
		const clientIds = [clientId];
		if ((hasCompanyGroupCardFeature || !hasSubaccountsV2 || hasSubaccountsFeature) && subAccountIds.length) {
			clientIds.push(...subAccountIds);
		}
		q.push({ a: 'client', c: 'eq', v: clientIds });
		if (excludedStageIds.length) {
			q.push({ a: 'stage', c: 'ne', v: excludedStageIds });
		}
		if (widgetType === WidgetType.TOTAL_RECURRING || widgetType === WidgetType.RECURRING) {
			q.push({ a: 'date', c: 'eq', v: ['Last5Years'] });
		}

		const cancelable = makeCancelable(
			ReportWidget.find(widgetType, {
				grouping: showGoal && grouping !== Groupings.CLIENT ? [grouping, 'client'] : [grouping],
				aggregateSubAccounts: !isGroupBySubAccount,
				q
			})
		);

		cancelable.promise
			.then(widgetData => {
				if (grouping === Groupings.MONTH && widgetData?.data?.rows) {
					widgetData.data.rows = fillEmptyMonths(widgetData.data.rows);
				}

				if (
					grouping !== Groupings.USER &&
					grouping !== Groupings.STAGE &&
					grouping !== Groupings.CLIENT &&
					grouping !== Groupings.PRODUCT &&
					grouping !== Groupings.PRODUCT_CATEGORY
				) {
					widgetData.data.rows.reverse();
				}

				setWidgetData({ data: widgetData.data, loading: false, error: false });
				setWidgetError(false);
			})
			.catch(error => {
				logError(error, 'Could not get widget data');
				setWidgetError(true);
			});

		return cancelable;
	};

	useEffect(() => {
		const events = [
			'order.added',
			'order.updated',
			'order.deleted',
			'account.updated',
			'agreement.added',
			'agreement.updated',
			'agreement.deleted',
			'agreementGroup.added',
			'agreementGroup.updated',
			'agreementGroup.deleted'
		];
		const unsubscribeFunctions = events.map(event => EventBus.on(event, fetchWidgetData));

		const metaPromise = makeCancelable(ReportWidget.getMeta());

		metaPromise.promise
			.then(res => setWidgetMeta(res?.data))
			.catch(err => logError(err, 'Could not get widget meta'));

		return () => {
			metaPromise.cancel();
			unsubscribeFunctions.forEach(fn => fn());
		};
	}, [grouping, widgetType]);

	useEffect(() => {
		const widgetPromise = fetchWidgetData();
		return () => {
			widgetPromise.cancel();
		};
	}, [grouping, widgetType]);

	if (!widgetData) {
		return widgetError ? (
			<div className={classes.elem('graphError').b()}>
				<Link onClick={() => fetchWidgetData()} align="center" color="red">
					{T('reportcenter.failedToLoadWidget')}
				</Link>
			</div>
		) : null;
	}

	const widgetConfig = {
		id: 0,
		filters: [],
		legend: false,
		width: 4,
		x: 0,
		goal: showGoal && !hideGoal,
		displayType: 'columnChart',
		groupBy: showGoal && grouping !== Groupings.CLIENT ? [grouping, 'client'] : [grouping],
		height: height,
		type: 'SALES',
		title: '',
		y: 1
	};

	return (
		<div className={classes.b()}>
			{widgetMeta ? (
				<ReportcenterWidget
					widgetConfig={widgetConfig}
					drilldown={false}
					widgetData={widgetData}
					getWidgetData={() => null}
					widgetMetaObj={widgetMeta}
				/>
			) : (
				<Loader />
			)}
		</div>
	);
};

export default SalesGraph;
